<template>
  <div class="card card-custom card-shadowless border assemble-inspect-panel mb-5">
    <div class="card-header border-0 py-4">
      <div class="card-title align-items-center">
        <span class="card-label font-weight-bolder text-dark">
          <i class="fa fa-clipboard-check mr-2 text-primary"></i>
          Pre-post checks
        </span>
      </div>
      <div class="card-toolbar">
        <button
          type="button"
          class="btn btn-sm btn-light-primary font-weight-bold"
          :disabled="loading"
          @click.prevent="loadInspect"
        >
          <i class="fa fa-sync" :class="{ 'fa-spin': loading }"></i>
          Refresh
        </button>
      </div>
    </div>

    <div class="card-body pt-2 pb-4">
      <div v-if="loading" class="text-muted font-size-sm">
        <i class="fa fa-spinner fa-spin mr-1"></i> Running pre-post checks…
      </div>

      <b-alert v-else-if="loadError" variant="warning" show class="mb-0">
        <div class="text-dark">{{ loadError }}</div>
      </b-alert>

      <div v-else-if="diagnostic && diagnostic.ok === false">
        <b-alert variant="warning" show class="mb-0">
          <div class="text-dark">
            {{ diagnostic.error || "Pre-post checks could not be completed for this billing line." }}
          </div>
        </b-alert>
      </div>

      <div v-else-if="diagnostic && diagnostic.ok">
        <div class="text-muted font-size-sm mb-3">
          Read-only view of what would be sent, and whether posting is clear to proceed.
        </div>

        <b-alert
          :variant="insightValid ? 'success' : 'warning'"
          show
          class="mb-3"
        >
          <div class="text-dark">
            <strong>{{ insightValid ? "Pre-check passed." : "Pre-check did not pass." }}</strong>
            <span v-if="insightErrors.length" class="d-block mt-1">
              {{ insightErrors.join("; ") }}
            </span>
          </div>
        </b-alert>

        <table class="table table-sm table-bordered mb-0">
          <tr v-for="(row, index) in trustRows" :key="index">
            <td width="6%" class="text-center align-middle">
              <i
                v-if="row.pass"
                class="fa fa-check-circle text-success font-size-h5"
              ></i>
              <i
                v-else
                class="fa fa-times-circle text-danger font-size-h5"
              ></i>
            </td>
            <td>
              <div
                class="font-weight-bold"
                :class="row.pass ? 'text-dark' : 'text-danger'"
              >
                {{ row.label }}
              </div>
              <div class="font-size-sm text-muted">{{ row.detail }}</div>
            </td>
          </tr>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { assembleInspect } from "@/services/insight-post-service";
import { STATUS_NONE } from "@/copy/posting-copy";

export default {
  name: "AssembleInspectPanel",

  props: {
    billingLineId: {
      type: String,
      required: true
    }
  },

  data: function() {
    return {
      loading: false,
      loadError: null,
      diagnostic: null
    };
  },

  mounted: function() {
    this.loadInspect();
  },

  watch: {
    billingLineId: function() {
      this.loadInspect();
    }
  },

  computed: {
    trusts: function() {
      return (this.diagnostic && this.diagnostic.trusts) || {};
    },

    insightValid: function() {
      const ir = this.diagnostic && this.diagnostic.insightResponse;
      return !!(ir && ir.isValid);
    },

    insightErrors: function() {
      const ir = this.diagnostic && this.diagnostic.insightResponse;
      return ir && Array.isArray(ir.errors) ? ir.errors : [];
    },

    trustRows: function() {
      const t = this.trusts;
      const status = t.existingPostingStatus;
      let statusDetail = STATUS_NONE;
      if (status && status !== "none") {
        statusDetail = "Current posting state: " + status + ".";
      }
      return [
        {
          label: "Draft creation is switched on",
          pass: !!t.commitEnabled,
          detail: t.commitEnabled
            ? "Drafts can be created from this app."
            : "Draft creation is switched off right now."
        },
        {
          label: "Posting is switched on",
          pass: !!t.postToAccountEnabled,
          detail: t.postToAccountEnabled
            ? "Posting from this app is switched on."
            : "Posting from this app is switched off right now."
        },
        {
          label: "Contract version matches",
          pass: !!t.contractVersionPinned,
          detail: t.contractVersionPinned
            ? "The expected INSIGHT contract is pinned."
            : "The expected INSIGHT contract is not pinned."
        },
        {
          label: "Policy is on the approved list",
          pass: !!t.policyAllowlisted,
          detail: t.policyAllowlisted
            ? "This policy is approved for posting from the app."
            : "This policy is not on the short approved list."
        },
        {
          label: "Transaction is an endorsement or renewal",
          pass: !!t.isPatchEndpoint,
          detail: t.isPatchEndpoint
            ? "This transaction can be posted on an existing policy."
            : "New-business create mode is out of scope for this post path."
        },
        {
          label: "Who we're invoicing is identified",
          pass: !!t.hasResolvedDebtorId,
          detail: t.hasResolvedDebtorId
            ? "The account to invoice is resolved."
            : "The account to invoice is missing."
        },
        {
          label: "Draft status",
          pass: status === "none" || status === "authorised",
          detail: statusDetail
        }
      ];
    }
  },

  methods: {
    loadInspect: function() {
      const id = String(this.billingLineId || "").trim();
      if (!id) {
        return;
      }
      this.loading = true;
      this.loadError = null;
      assembleInspect(id)
        .then(result => {
          this.diagnostic = result || null;
          this.loading = false;
          this.$emit("inspect-loaded", {
            diagnostic: this.diagnostic,
            expectedDebtorId: extractDebtorId(this.diagnostic)
          });
        })
        .catch(() => {
          this.loadError =
            "Unable to load the pre-post checks for this billing line.";
          this.loading = false;
        });
    }
  }
};

function extractDebtorId(diagnostic) {
  if (!diagnostic || !diagnostic.ok || !diagnostic.body) {
    return null;
  }
  const body = diagnostic.body;
  const ptx = body.PolicyTransactionWizard
    ? body.PolicyTransactionWizard.PolicyTransaction
    : body.PolicyTransaction;
  if (!ptx) {
    return null;
  }
  const id = ptx.PolicyClientId != null ? ptx.PolicyClientId : ptx.DebtorId;
  if (id === null || id === undefined || id === "" || id === 0) {
    return null;
  }
  return id;
}
</script>
